import { forwardRef, Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { DependencyTreeCanActivate } from 'app/core/guards/dependency-tree.guard';
import { OrderFilterValuesLoadGuard } from 'app/core/guards/order-filter-values-load.guard';
import { AuthorizationGuard } from 'app/core/store/session/authorization/authorization.resolve';
import { OrderTemplateCreateStartAction } from 'app/core/store/template/template.actions';
import { logger } from 'app/core/util/logger';
import { Observable, of } from 'rxjs';
import * as fromRoot from '..';
import { SessionState } from '../session/session.interface';
import { OrderDetailCreateStartAction } from './order-detail.actions';
import { goAction } from '../router/router.actions';
import { first, map, switchMap } from 'rxjs/operators';

/**
 * Guard to async load an order before following a route
 */
@Injectable({ providedIn: 'root' })
export class CreateOrderGuard implements CanActivate, DependencyTreeCanActivate {
  private store: Store<fromRoot.RootState>;

  constructor(@Inject(forwardRef(() => Store)) store) {
    this.store = store;
  }

  canActivate(route: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): Observable<boolean> {
    logger.debug(`In CreateOrderGuard`);

    return this.store.pipe(
      first(),
      switchMap(state => {
        if (!state.session.authorization.data.ORDER.CREATE) {
          this.store.dispatch(goAction({ path: '/' }));
          return of(false);
        }

        return this.store.select('session').pipe(
          first(),
          map((session: SessionState) => session.selectedCustomer.data),
          switchMap(selectedCustomer => {
            if (route.data.createTemplate) {
              this.store.dispatch(
                new OrderTemplateCreateStartAction({
                  selectedCustomer,
                  isSuperUser: state.session.userContext.isSuperUser,
                })
              );
            } else {
              this.store.dispatch(
                new OrderDetailCreateStartAction({
                  selectedCustomer,
                  date: route.queryParamMap?.get('date'),
                  restrictions: state.customerInfo.restrictions,
                  preventCheckingContractDetails: route.data.preventCheckingContractDetails,
                  isSuperUser: state.session.userContext.isSuperUser,
                })
              );
            }
            return of(true);
          })
        );
      })
    );
  }

  getDependencies(): any {
    return [OrderFilterValuesLoadGuard, AuthorizationGuard];
  }

  getDependencyRouteData(): any {
    return {
      authorization: 'ORDER.CREATE',
    };
  }
}
